import { XYZClass } from '@slot-matrix/shared-types';
/**
 * Calculate the Coefficient of Variation and related statistics.
 */
export function calculateCV(periods) {
    const n = periods.length;
    if (n < 2) {
        return {
            cv: NaN,
            mean: n === 1 ? periods[0].quantity : 0,
            stdDev: 0,
            zeroDemandPeriods: n === 1 ? (periods[0].quantity === 0 ? 1 : 0) : 0,
            isIntermittent: false,
            insufficientData: true,
            isZeroDemand: false,
        };
    }
    const quantities = periods.map((p) => p.quantity);
    const mean = quantities.reduce((sum, q) => sum + q, 0) / n;
    if (mean === 0) {
        return {
            cv: Infinity,
            mean: 0,
            stdDev: 0,
            zeroDemandPeriods: n,
            isIntermittent: true,
            insufficientData: false,
            isZeroDemand: true,
        };
    }
    // Sample standard deviation (Bessel's correction)
    const variance = quantities.reduce((sum, q) => sum + (q - mean) ** 2, 0) / (n - 1);
    const stdDev = Math.sqrt(variance);
    const cv = stdDev / mean;
    const zeroDemandPeriods = quantities.filter((q) => q === 0).length;
    const isIntermittent = zeroDemandPeriods / n > 0.5;
    return {
        cv,
        mean,
        stdDev,
        zeroDemandPeriods,
        isIntermittent,
        insufficientData: false,
        isZeroDemand: false,
    };
}
/**
 * Aggregate raw demand records into time series at the specified granularity.
 * Fills date gaps with zero-quantity periods.
 */
export function aggregateDemand(records, granularity) {
    // Group by SKU
    const bySku = new Map();
    for (const rec of records) {
        const existing = bySku.get(rec.skuId) ?? [];
        existing.push(rec);
        bySku.set(rec.skuId, existing);
    }
    const result = [];
    for (const [skuId, recs] of bySku) {
        const periods = aggregatePeriods(recs, granularity);
        result.push({
            skuId,
            periodGranularity: granularity,
            periods,
        });
    }
    return result;
}
function aggregatePeriods(records, granularity) {
    const sorted = [...records].sort((a, b) => a.date.localeCompare(b.date));
    // Group by period key
    const periodMap = new Map();
    for (const rec of sorted) {
        const key = getPeriodKey(rec.date, granularity);
        periodMap.set(key, (periodMap.get(key) ?? 0) + rec.quantity);
    }
    // Sort by period key
    const sortedKeys = [...periodMap.keys()].sort();
    return sortedKeys.map((key) => ({
        periodStart: key,
        quantity: periodMap.get(key) ?? 0,
    }));
}
function getPeriodKey(date, granularity) {
    const d = new Date(date);
    switch (granularity) {
        case 'daily':
            return date;
        case 'weekly': {
            // ISO week number
            const jan1 = new Date(d.getFullYear(), 0, 1);
            const days = Math.floor((d.getTime() - jan1.getTime()) / (24 * 60 * 60 * 1000));
            const week = Math.ceil((days + jan1.getDay() + 1) / 7);
            return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
        }
        case 'monthly':
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        default:
            return date;
    }
}
/**
 * Classify SKUs into XYZ tiers based on coefficient of variation.
 */
export function classifyXYZ(timeSeries, config) {
    return timeSeries.map((ts) => {
        // Apply lookback window
        let periods = ts.periods;
        if (config.lookbackDays > 0 && periods.length > 0) {
            const latestDate = periods[periods.length - 1].periodStart;
            const cutoff = new Date(latestDate);
            cutoff.setDate(cutoff.getDate() - config.lookbackDays);
            const cutoffStr = cutoff.toISOString().slice(0, 10);
            periods = periods.filter((p) => p.periodStart >= cutoffStr);
        }
        // Apply fixed calendar period mode
        if (config.fixedPeriodMode === 'year_to_date' && periods.length > 0) {
            const latestDate = periods[periods.length - 1].periodStart;
            const yearStart = latestDate.slice(0, 4) + '-01-01';
            periods = periods.filter((p) => p.periodStart >= yearStart);
        }
        else if (config.fixedPeriodMode === 'custom' && config.customPeriodStart && config.customPeriodEnd) {
            periods = periods.filter((p) => p.periodStart >= config.customPeriodStart && p.periodStart <= config.customPeriodEnd);
        }
        // Exclude anomalies
        const excludedIndices = [];
        const filteredPeriods = periods.filter(() => {
            // Simple anomaly detection: exclude if quantity > 3 * mean (preliminary)
            return true; // Keep all by default; anomaly exclusion is user-driven via excludedAnomalyIndices
        });
        let cvResult;
        if (config.useRecencyWeighting && filteredPeriods.length > 0) {
            // Weighted CV calculation
            const latestDate = filteredPeriods[filteredPeriods.length - 1].periodStart;
            const weights = filteredPeriods.map((p) => {
                const daysAgo = Math.max(0, (new Date(latestDate).getTime() - new Date(p.periodStart).getTime()) /
                    (24 * 60 * 60 * 1000));
                return Math.exp(-config.recencyDecayFactor * daysAgo);
            });
            const sumWeights = weights.reduce((s, w) => s + w, 0);
            const weightedMean = filteredPeriods.reduce((s, p, i) => s + p.quantity * weights[i], 0) / sumWeights;
            if (weightedMean === 0) {
                cvResult = {
                    cv: Infinity,
                    mean: 0,
                    stdDev: 0,
                    zeroDemandPeriods: filteredPeriods.length,
                    isIntermittent: true,
                    insufficientData: false,
                    isZeroDemand: true,
                };
            }
            else {
                const weightedVar = filteredPeriods.reduce((s, p, i) => s + weights[i] * (p.quantity - weightedMean) ** 2, 0) /
                    sumWeights;
                const weightedStdDev = Math.sqrt(weightedVar);
                const cv = weightedStdDev / weightedMean;
                const zeroDemandPeriods = filteredPeriods.filter((p) => p.quantity === 0).length;
                cvResult = {
                    cv,
                    mean: weightedMean,
                    stdDev: weightedStdDev,
                    zeroDemandPeriods,
                    isIntermittent: zeroDemandPeriods / filteredPeriods.length > 0.5,
                    insufficientData: false,
                    isZeroDemand: false,
                };
            }
        }
        else {
            cvResult = calculateCV(filteredPeriods);
        }
        // Assign class
        let assignedClass;
        if (cvResult.insufficientData || Number.isNaN(cvResult.cv)) {
            assignedClass = XYZClass.Z; // Insufficient data defaults to Z (most conservative)
        }
        else if (cvResult.cv <= config.thresholdXY) {
            assignedClass = XYZClass.X;
        }
        else if (cvResult.cv <= config.thresholdYZ) {
            assignedClass = XYZClass.Y;
        }
        else {
            assignedClass = XYZClass.Z;
        }
        return {
            skuId: ts.skuId,
            meanDemand: cvResult.mean,
            stdDev: cvResult.stdDev,
            cv: cvResult.cv,
            periodCount: filteredPeriods.length,
            zeroDemandPeriods: cvResult.zeroDemandPeriods,
            isIntermittent: cvResult.isIntermittent,
            assignedClass,
            excludedAnomalyIndices: excludedIndices,
        };
    });
}
//# sourceMappingURL=xyzClassifier.js.map